/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {Asset, CRUDPermissions} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import * as actions from './state';

// defines the asset row editor prop definitions ...
export interface AssetRowEditorTypes {
  asset: Asset;
  includeGlobalSearch?: boolean;
  assetsPermissions?: CRUDPermissions;
}

/**
 * Defines the AssetRowEditor component. This component contains the cell actions
 * for admin assets grid.
 *
 * ```typescript
 *
 *   import {AssetRowEditor} from './AssetRowEditor';
 *
 *   <AssetRowEditor .../>
 *
 * ```
 */
export const AssetRowEditor = ({
  asset,
  assetsPermissions,
  includeGlobalSearch = false
}: AssetRowEditorTypes) => {
  const dispatch    = useDispatch();
  const cellMenuRef = useRef(null);
  const {
    hasReadPermission,
    hasUpdatePermission,
    hasDeletePermission
  } = assetsPermissions || {};

  function closeMenu() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  const onAction = function(type: string, viewMode: boolean) {
    return function() {
      if (viewMode) {
        dispatch(actions[type](asset, true, viewMode));
      } else {
        dispatch(actions[type](asset));
      }
      closeMenu();
    };
  };

  return (
    <>
      <CellMenu ref={cellMenuRef} menuItems={
        <div>
          <CellMenuItem icon={<ViewRecordIcon />}
            onClick={onAction('editAdminAsset', true)}
            disabled={!hasReadPermission}
            label={translate('admin-assets:grid.menu.view')} />
          <CellMenuItem icon={<MapEditIcon />}
            onClick={onAction('editAdminAsset', false)}
            disabled={!hasUpdatePermission}
            label={translate('admin-assets:grid.menu.edit')} />
          <CellMenuItem icon={<NotificationTimeIcon />}
            onClick={onAction('auditAdminAsset', false)}
            label={translate('admin-assets:grid.menu.audit')} />
          {!includeGlobalSearch && (
            <CellMenuItem icon={<SystemDeleteIcon />}
              onClick={onAction('deleteAdminAsset', false)}
              disabled={!hasDeletePermission}
              label={translate('admin-assets:grid.menu.delete')} />
          )}
        </div>
      } />
    </>
  );
};
